import type { EpisodeView } from "@supermanus/document-model";
import { useEffect, useState, type Ref } from "react";
import { createPortal } from "react-dom";
import { InlineEditable } from "./InlineEditable";
import {
  reorderByBefore,
  usePointerReorder,
} from "./usePointerReorder";

export const EPISODE_TABS: { id: EpisodeView; label: string }[] = [
  { id: "timing", label: "Sceneliste" },
  { id: "outline", label: "Rundown" },
  { id: "script", label: "Manus" },
  { id: "sources", label: "Noter" },
  { id: "crew", label: "Creditliste" },
  { id: "programtext", label: "Programtekst" },
  { id: "status", label: "Status" },
];

/** Map the retired episode cover onto Status. */
export function workspaceView(view: EpisodeView): EpisodeView {
  return view === "home" ? "status" : view;
}

export function episodeFolderLandscape(view: EpisodeView): boolean {
  return workspaceView(view) === "sources";
}

export function orderedEpisodeTabs(
  order?: EpisodeView[],
): { id: EpisodeView; label: string }[] {
  const byId = new Map(EPISODE_TABS.map((tab) => [tab.id, tab]));
  const out: { id: EpisodeView; label: string }[] = [];
  const seen = new Set<EpisodeView>();
  for (const id of order ?? []) {
    const tab = byId.get(id);
    if (!tab || seen.has(id)) continue;
    seen.add(id);
    out.push(tab);
  }
  for (const tab of EPISODE_TABS) {
    if (!seen.has(tab.id)) out.push(tab);
  }
  return out;
}

export function EpisodeFolderTabs({
  view,
  onChangeView,
  tabOrder,
  onReorderTabs,
  onCopyTabLink,
  episodeTitle,
  readOnly,
  onRenameEpisode,
}: {
  view: EpisodeView;
  onChangeView: (view: EpisodeView) => void;
  tabOrder?: EpisodeView[];
  onReorderTabs?: (order: EpisodeView[]) => void;
  onCopyTabLink?: (view: EpisodeView) => void;
  episodeTitle?: string;
  readOnly?: boolean;
  onRenameEpisode?: (title: string) => void;
}) {
  const active = workspaceView(view);
  const tabs = orderedEpisodeTabs(tabOrder);
  const { listRef, dragId, dropBeforeId, beginDrag } = usePointerReorder({
    axis: "flow",
    onMove: (id, beforeId) => {
      if (!onReorderTabs) return;
      const next = reorderByBefore(tabs, id, beforeId);
      onReorderTabs(next.map((tab) => tab.id));
    },
    onActivate: (id) => onChangeView(id as EpisodeView),
  });
  const [tabMenu, setTabMenu] = useState<{
    view: EpisodeView;
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    if (!tabMenu) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setTabMenu(null);
    };
    const onDown = (e: MouseEvent) => {
      if (e.button === 2) return;
      setTabMenu(null);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("mousedown", onDown);
    };
  }, [tabMenu]);

  return (
    <>
      {episodeTitle != null ? (
        <div className="sm-folder-episode-row">
          <InlineEditable
            className="sm-folder-episode"
            value={episodeTitle}
            onChange={(title) => onRenameEpisode?.(title)}
            readOnly={readOnly || !onRenameEpisode}
            placeholder="Unavngiven episode"
            title="Klik for at omdøbe episode"
          />
        </div>
      ) : null}
      <div
        ref={listRef as Ref<HTMLDivElement>}
        className={`sm-folder-tabs${dragId ? " is-reordering" : ""}${
          dragId && dropBeforeId === null ? " is-drop-end" : ""
        }`}
        role="tablist"
        aria-label="Episode"
      >
        {tabs.map((tab) => {
          const selected = active === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              role="tab"
              data-step-id={tab.id}
              aria-selected={selected}
              title={
                onCopyTabLink
                  ? "Træk for at ændre rækkefølgen. Højreklik for at kopiere link."
                  : "Træk for at ændre rækkefølgen"
              }
              className={`sm-folder-tab${selected ? " is-active" : ""}${
                dragId === tab.id ? " is-dragging" : ""
              }${dropBeforeId === tab.id ? " is-drop-before" : ""}`}
              onPointerDown={(e) => beginDrag(tab.id, e)}
              onContextMenu={
                onCopyTabLink
                  ? (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setTabMenu({
                        view: tab.id,
                        x: e.clientX,
                        y: e.clientY,
                      });
                    }
                  : undefined
              }
            >
              {tab.label}
            </button>
          );
        })}
      </div>
      {tabMenu && onCopyTabLink
        ? createPortal(
            <div
              className="sm-ctx-menu"
              role="menu"
              style={{ left: tabMenu.x, top: tabMenu.y }}
              onMouseDown={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="sm-ctx-menu-item"
                role="menuitem"
                onClick={() => {
                  onCopyTabLink(tabMenu.view);
                  setTabMenu(null);
                }}
              >
                Kopiér link til fane
              </button>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
